import { useState, useRef } from "react";
import { 
	Flex, 
	Modal,
	ModalOverlay,
	ModalContent,
	ModalCloseButton,
	Text,
	Textarea,
	Input,
	useToast,
	Button,
	Select,
	Image,
	Switch
} from "@chakra-ui/react";
import { isEmpty, toBase64 } from "../../utils";
import { useEditQuranPerson, useDeleteQuranPerson, useAddKhatmiya } from "../../hooks";

export function EditPersonModal({ onClose, data }) {
	const toast = useToast();
	const fileRef = useRef();

	const { mutateAsync: editPerson } = useEditQuranPerson();
	const { mutateAsync: deletePerson } = useDeleteQuranPerson();
	const { mutateAsync: onAddKhatmiya } = useAddKhatmiya();

 	const [loading, setLoading] = useState(false);
	const [language, setLanguage] = useState("english");

	const [image, setImage] = useState({
		"url": data.image
	});
	const [rejectedReason, setRejectedReason] = useState(data.rejectedReason);
	const [type, setType] = useState(data.type);
	const [status, setStatus] = useState(data.status);
	const [name, setName] = useState(data.name);
	const [closedJuz, setClosedJuz] = useState(data.closed)
	const [totalKhatmat, setTotalKhatmat] = useState(data.totalKhatmat);
	const [featured, setFeatured] = useState(data.featured);

	const languges = ['English', 'Arabic', 'Persian'];


	async function handleFile(e) {
		let files = e.target.files;
		let file = files[0];

		setImage({
			"base64": await toBase64(file),
			"type": file.type,
			"size": file.size,
			"name": file.name
		})
	}

	function handleName(value) {
		let _name = {...name};
		_name[language] = value;
		setName(_name)
	}

	function onJuzDelete(title){
		console.log(title)
	}

	async function onDeletePerson() {
		const confirmed = window.confirm("Are you sure you want to delete this person?");
		if (confirmed) {
			setLoading(true);
			try {
				await deletePerson({
					id: data.id
				})
				onClose();
				toast({
					status:"success",
					title: "Person was successfully deleted!",
					position: 'top',
				})	
			}
			catch(error) {
				toast({
					status:"error",
					title: error.message,
					position: 'top',
				})				
			}
			setLoading(false);			
		}
	}

	async function handleSubmit(){
		setLoading(true);
		let errors = validate({ image, status, name, rejectedReason });
		if (errors.length > 0) {
			toast({
				status:"error",
				title: "Some fields are missing",
				description: errors.join(', '),
				position: 'top',
			})	
			setLoading(false);
			return
		}
		try {
			await editPerson({
				id: data.id,
				name,
				image,
				status,
				type,
				totalKhatmat: 1,
				rejectedReason,
				featured,
				closed: closedJuz
			})
			onClose();
			toast({
				status:"success",
				title: "Person was updated successfully!",
				position: 'top',
			})	
		}
		catch(error) {
			toast({
				status:"error",
				title: error.message,
				position: 'top',
			})				
		}

		setLoading(false);
	}

	async function createKhatmiya() {
		setLoading(true);
		
		try {
			await onAddKhatmiya({
				id: data.id,
			})
			onClose();
			toast({
				status:"success",
				title: "A new khatmiya has been created!",
				position: 'top',
			})	
		}
		catch(error) {
			toast({
				status:"error",
				title: error.message,
				position: 'top',
			})				
		}
		setLoading(false);
	}

	return (
		<Modal isOpen={true} onClose={onClose} size="lg" isCentered motionPreset='slideInBottom'>
			<ModalOverlay />
			<ModalContent borderRadius="15px">
				<ModalCloseButton mr="-5px" borderRadius="30px" color="lightText" />
				<Flex p="20px 15px 0" direction="column">
					<Text variant="title" fontSize="21px">Edit person</Text>
					<Text variant="subtitle" fontSize="15px" mb="10px">Upload their image, add their name and the current status</Text>
					<Flex onClick={() => fileRef.current.click()} cursor="pointer" mb="20px" w="100%" align="center" direction="column" justify="center" border="1px solid #f3f3f3" h={image ? "230px" : "170px"} mt="10px" borderRadius="10px">
						{
							image ?
							<Image
								src={image.base64 || image.url}
								w="100%"
								h="100%"
								objectFit="contain"
								alt="Image"
							/>
							:
							<>
								<Flex w="40px" h="40px" borderRadius="30px" color="lightText" bg="light" align="center" justify="center">
									<div><i className="fa-solid fa-image"></i></div>
								</Flex>
								<Text color="lightText" variant="subtitle" mt="5px" opacity="1">Upload your image</Text>
							</>
						}
					</Flex>
					<Flex mb="10px">
						{
							languges.map(lang => (
								<Flex onClick={() => setLanguage(lang.toLowerCase())} key={lang} p="5px 10px" bg={language == lang.toLowerCase() ? "dark" : "white"} borderRadius="30px" cursor="pointer">
									<Text color={language == lang.toLowerCase() ? "white" : "lightText"} textTransform="capitalize" fontSize="11px">{lang}</Text>
								</Flex>
							))
						}
					</Flex>

					<Flex direction="column" flex="1">
						<Text variant="subtitle">Name</Text>
						<Input
							type="text"
							placeholder="Full Name"
							mt="5px"
							value={name[language] || ""}
							onChange={(e) => handleName(e.target.value)}
							fontWeight="500"
							h="50px"
							p="10px"
							autoFocus={true}
							_focus={{
								borderColor:"primary",
								borderWidth:"2px"
							}}
						/>
					</Flex>

					<Flex mt="20px" align="center" justify="center">
						<Text flex="1" variant="subtitle">Featured</Text>
						<Switch isChecked={featured} onChange={(e) => setFeatured(e.target.checked)} colorScheme="green" size='md' />
					</Flex>	
					
					<Flex direction="row" flex="1" mt="15px" align="center" justify="center">
						<Text variant="subtitle" flex="1">Status</Text>
						<Select value={status} onChange={(e) => setStatus(e.target.value)} flex="1" h="50px" variant='outline' fontWeight="500" fontSize="15px" placeholder='Select status'>
							<option value="live">Live</option>
							<option value="not-live">Not Live</option>
							<option value="pending">Pending</option>
							<option value="finished">Finished</option>
							<option value="rejected">Rejected</option>
						</Select>						
					</Flex>
					{
						status == "rejected" && (
							<Flex direction="column" w="full" mt="15px" align="start" justify="center">
								<Text variant="subtitle">Reason for rejection</Text>
								<Select value={rejectedReason} mt="10px" onChange={(e) => setRejectedReason(e.target.value)} flex="1" h="50px" variant='outline' fontWeight="500" fontSize="15px" placeholder='Select reason'>
									<option value="Image does not clearly show the persons face.">Image does not clearly show the persons face.</option>
									<option value="Image has political images which are not allowed.">Image has political images which are not allowed.</option>
									<option value="The first and last name is invalid.">The first and last name is invalid.</option>
									<option value="This person has already been added.">This person has already been added.</option>
								</Select>	
							</Flex>
						)
					}
					<Flex direction="row" flex="1" mt="15px" align="center" justify="center">
						<Text variant="subtitle" flex="1">Type</Text>
						<Select disabled={status == "live"} value={type} onChange={(e) => setType(e.target.value)} flex="1" h="50px" variant='outline' fontWeight="500" fontSize="15px" placeholder='Select status'>
							<option value="juz">Juz</option>
							<option value="surah">Surah</option>
						</Select>						
					</Flex>

					<Text onClick={onDeletePerson} cursor="pointer" textAlign="center" variant="subtitle" color="error" opacity="1" mt="40px" fontSize="14px" fontWeight="600">Delete person</Text>
					<Flex flex="1" w="100%" p="10px 0" borderTop="1px solid #f3f3f3" mt="10px" gap="15px">
						<Flex as={Button} variant="white" isLoading={loading} onClick={createKhatmiya} flex="1" h="50px" w="100%" p="14px 0" borderRadius="5px" align="center" justify="center" cursor="pointer">
							<Text variant="subtitle" color="gray" fontSize="16px" fontWeight="600" opacity="1">Add Khatmiya</Text>
						</Flex>

						<Flex as={Button} variant="primary" isLoading={loading} onClick={handleSubmit} flex="2" h="50px" w="100%" p="14px 0" borderRadius="5px" align="center" justify="center" cursor="pointer">
							<Text variant="subtitle" color="white" fontSize="16px" fontWeight="600" opacity="1">Save person</Text>
						</Flex>
					</Flex>
				</Flex>
			</ModalContent>
			<input type="file" hidden style={{ display: "none" }} accept=".jpg, .png, .jpeg" ref={fileRef} onChange={handleFile} />
		</Modal>
	)
}

function ClosedJuzRow({ data, onDelete }){
	return (
		<Flex direction="row" p="7px 0" align="center" justify="center" borderBottom="1px solid" borderBottomColor="light">
			<Text fontSize="15px" fontWeight="600" mr="15px">{data.title}</Text>
			<Text fontSize="14px" fontWeight="500" flex="1">{data.status}</Text>
			<Text variant="subtitle">{data.user.FirstName}</Text>

			<Flex onClick={onDelete} ml="10px" w="23px" h="23px" bg="light" color="lightText" borderRadius="30px" align="center" justify="center" cursor="pointer">
				<div><i className="fa-solid fa-circle-xmark"></i></div>
			</Flex>
		</Flex>
	)
}

function validate({ image, name, status, rejectedReason }){
	let errors = [];

	if (isEmpty(status) == true) {
		errors.push('Status')
	} else if (status == "rejected") {

		if (isEmpty(rejectedReason)) {
			errors.push('Rejected Reason');
		}
		return errors;
	}	

	if (isEmpty(name['english']) == true) {
		errors.push('Name: English')
	}
	if (isEmpty(name['arabic']) == true) {
		errors.push('Name: Arabic')
	}
	if (isEmpty(name['persian']) == true) {
		errors.push('Name: Persian')
	}
	if (!image) {
		errors.push('Image')
	}


	return errors;
}












